import React, { useEffect, useState } from "react";
import BasedHeader from "../components/BasedHeader";
import {
  useContract,
  useAddress,
  useOwnedNFTs,
  ConnectWallet,
} from "@thirdweb-dev/react";
import { sdk } from "../lib/thirdweb";
import { Dialog, Transition } from "@headlessui/react";
import { contractConst } from "../consts/parameters";

interface PonkAttribute {
  trait_type: string;
  value: string;
  rarityPercentage?: string;
}

interface PonkMetadata {
  id: string;
  name: string;
  image: string;
  attributes: PonkAttribute[];
}

interface Ponk {
  metadata: PonkMetadata;
  rarityScore: number;
  rarityRank: number;
}

const UserDashboard: React.FC = () => {
  const address = useAddress();
  const { contract } = useContract(contractConst);
  const { data: ownedNFTsRaw, isLoading } = useOwnedNFTs(contract, address);
  const [ownedPonks, setOwnedPonks] = useState<Ponk[]>([]);
  const [selectedPonk, setSelectedPonk] = useState<Ponk | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  useEffect(() => {
    const fetchAndCalculateRanks = async () => {
      setIsFetching(true);
      try {
        const nftCollection = await sdk.getContract(contractConst, "nft-collection");

        let allPonks: { metadata: any }[] = [];
        let cursor: string | undefined = undefined;

        while (true) {
          const batch = await nftCollection.erc721.getAll({
            start: cursor ? parseInt(cursor) : 0,
            count: 50,
          });

          allPonks = [...allPonks, ...batch];

          if (batch.length < 50) break;
          cursor = (allPonks.length).toString();
        }

        const totalPonks = allPonks.length;
        console.log(`✅ Fetched ${totalPonks} Ponks from contract`);

        const traitCounts: Record<string, Record<string, number>> = {};
        allPonks.forEach((nft) => {
          const metadata = nft.metadata as PonkMetadata;

          if (Array.isArray(metadata.attributes)) {
            metadata.attributes.forEach(({ trait_type, value }) => {
              if (!traitCounts[trait_type]) traitCounts[trait_type] = {};
              traitCounts[trait_type][value] = (traitCounts[trait_type][value] || 0) + 1;
            });
          }
        });

        const ponksWithRarity: Ponk[] = allPonks.map((nft) => {
          const metadata = nft.metadata as PonkMetadata;
          let rarityScore = 0;

          const attributesWithRarity = Array.isArray(metadata.attributes)
            ? metadata.attributes.map(({ trait_type, value }) => {
                const count = traitCounts[trait_type]?.[value] || 1;
                let rarityPercentage = ((count / totalPonks) * 100).toFixed(2);
                rarityPercentage = rarityPercentage.endsWith(".00") ? parseInt(rarityPercentage).toString() : rarityPercentage;

                rarityScore += 1 / (count / totalPonks);
                return { trait_type, value, rarityPercentage };
              })
            : [];

          const idMatch = metadata.name.match(/#(\d+)$/);
          const imageId = idMatch ? idMatch[1] : metadata.id;
          const imageUrl = `https://basedponks.xyz/ponkimages/${imageId}.webp`;

          return {
            metadata: {
              id: metadata.id.toString(),
              name: metadata.name || `Ponk #${metadata.id}`,
              attributes: attributesWithRarity,
              image: imageUrl,
            },
            rarityScore,
            rarityRank: 0,
          };
        });

        const rankedPonks = ponksWithRarity
          .sort((a, b) => b.rarityScore - a.rarityScore)
          .map((ponk, index) => ({
            ...ponk,
            rarityRank: index + 1,
          }));

        const userPonkIds = ownedNFTsRaw
          ?.map((owned) => String(owned.metadata.id))
          .filter((id) => id !== null) as string[];

        console.log("✅ Owned Ponk IDs:", userPonkIds);

        const userPonks = rankedPonks.filter((ponk) =>
          userPonkIds.includes(ponk.metadata.id)
        );

        console.log(`✅ User Ponks Found: ${userPonks.length}`);
        setOwnedPonks(userPonks);
      } catch (error) {
        console.error("❌ Error fetching Ponks:", error);
      }
      setIsFetching(false);
    };

    if (ownedNFTsRaw) {
      fetchAndCalculateRanks();
    }
  }, [ownedNFTsRaw]);

  return (
    <>
      <BasedHeader />
      <div className="min-h-screen bg-gradient-radial from-gray-900 via-black to-black text-white pt-32">
        <div className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-7xl font-bold text-blue-400 drop-shadow-lg">
  Your Ponks
</h1>
          <p className="text-xl text-gray-400 mt-4">View your Based Ponks and their ranks! 🚀</p>

          {/* ✅ Display Total Ponks Owned */}
          <p className="text-3xl font-bold text-blue-400 mt-6">
  You Own: {ownedPonks.length} Ponks
</p>
        </div>

        <div className="container mx-auto px-4 pb-20">
          {!address ? (
            <div className="text-center">
              <p className="text-xl text-gray-400 mb-10">Please connect your wallet.</p>
              <ConnectWallet className="bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-4 px-8 rounded-xl hover:opacity-90 transition" />
            </div>
          ) : isLoading || isFetching ? (
            <p className="text-center text-gray-400">⏳ Loading...</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {ownedPonks.map((ponk) => (
                <div
                  key={ponk.metadata.id}
                  onClick={() => setSelectedPonk(ponk)}
                  className="glass p-3 rounded-xl transition hover:-translate-y-1 hover:shadow-xl cursor-pointer border-2 border-blue-500"
                >
                  <img src={ponk.metadata.image} alt={ponk.metadata.name} className="w-full h-auto rounded-lg mb-3" />
                  <h3 className="text-lg font-bold text-center">{ponk.metadata.name}</h3>
                  <p className="text-center text-sm">Rank #{ponk.rarityRank}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 🔥 Modal for Metadata */}
      <Transition appear show={!!selectedPonk} as={React.Fragment}>
        <Dialog onClose={() => setSelectedPonk(null)} className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <Transition.Child enter="ease-out duration-300" enterFrom="opacity-0 scale-90" enterTo="opacity-100 scale-100">
            <div className="glass p-6 rounded-xl max-w-sm w-full text-white relative">
              <button onClick={() => setSelectedPonk(null)} className="absolute top-2 right-2 text-white text-2xl font-bold">❌</button>
              <img src={selectedPonk?.metadata.image} alt={selectedPonk?.metadata.name} className="w-full h-auto rounded-lg mb-3" />
              <h2 className="text-2xl font-bold text-center">{selectedPonk?.metadata.name}</h2>
              {selectedPonk?.metadata.attributes.map(({ trait_type, value, rarityPercentage }, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span>{trait_type}</span>
                  <span>{value} ({rarityPercentage}%)</span>
                </div>
              ))}
            </div>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
};

export default UserDashboard;
